import { ElementType } from 'react';

import PadlockIcon from '@/assets/padlock.svg?react';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';

export interface BlockOverlayContentProps {
  title?: string;
  info?: string;
  subInfo?: string | React.ReactNode;
  /**
   * It is recommended to use either buttonLink or buttonConfirm, but not both.
   */
  buttonLink?: React.ReactNode;
  buttonConfirm?: {
    content: string;
    onClick?: () => void;
    withRightArrow?: boolean;
  };
  secondaryButtonContent?: string;
  onClose?: () => void;
  loading?: boolean;
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  loadingIcon?: boolean;
  buttonFlexDirection?: 'flex-row' | 'flex-col';
  disabled?: boolean;
  titleAs?: ElementType;
  descriptionAs?: ElementType;
}

const BlockOverlayContent: React.FC<BlockOverlayContentProps> = ({
  title,
  info,
  subInfo,
  buttonLink,
  buttonConfirm,
  secondaryButtonContent,
  onClose,
  loading,
  icon,
  loadingIcon,
  disabled,
  buttonFlexDirection = 'flex-row',
  titleAs: TitleTag = 'h3',
  descriptionAs: DescriptionTag = 'p',
}) => {
  const { i18n } = useLingui();

  const TitleIcon = icon ?? PadlockIcon;

  return (
    <>
      <div className={'flex flex-col items-center gap-y-3 text-center'}>
        {!loadingIcon && (
          <TitleIcon className={'size-6 text-blue-500 lg:size-[34px]'} />
        )}
        {loadingIcon && <div className="loader" />}
        <TitleTag className={'inline-flex items-center  gap-x-2 text-blue-600'}>
          {title}
        </TitleTag>
        {info && <DescriptionTag dangerouslySetInnerHTML={{ __html: info }} />}
        {subInfo && <p>{subInfo}</p>}
      </div>
      <div
        className={cn(
          'flex items-center justify-center gap-4',
          buttonFlexDirection
        )}
      >
        {buttonLink && buttonLink}
        {buttonConfirm && (
          <Button
            loading={loading}
            disabled={disabled || loading}
            size={'small'}
            onClick={buttonConfirm.onClick}
            data-testid="overlay-confirm-button"
          >
            {buttonConfirm.content}
            {buttonConfirm.withRightArrow && <ButtonRightArrow />}
            <ButtonLoading />
          </Button>
        )}
        {onClose && (
          <Button
            disabled={disabled || loading}
            onClick={onClose}
            intent={'secondary'}
            className="disabled:bg-white"
            size={'small'}
          >
            {secondaryButtonContent ?? i18n.t('Zamknij')}
          </Button>
        )}
      </div>
    </>
  );
};

export default BlockOverlayContent;
